import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import toast from "react-hot-toast";
import {formatTimeLabel} from '@/utils/textFormatting';
import {Tooltip} from 'flowbite-react';
import SubtitleOverlay from './SubtitleOverlay';
import {Subtitle} from "@/models/Subtitle";
import FFmpegService from "@/services/media/FFmpegService";
import {OPFSRemuxService} from "@/services/media/OPFSRemuxService";
import opfsService from "@/services/media/OPFSService";
import {isDesktop, isFirefox} from "@/utils/deviceUtils";

interface VideoPlayerProps {
    mediaFile: File | null;
    cacheKey: string;
    playbackSpeed: number;
    onTimeUpdate: (time: number) => void;
    onPlayStateChange: (isPlaying: boolean) => void;
    onVideoLoadingProgress: (val: number) => void;
    onVideoDimensionsChange: ({width, height}: { width: number, height: number}) => void;
    onMediaError: () => void;
    onTimeSeek: (time: number) => void;
    onPlaybackSpeedChange: (speed: number) => void;
    onSubtitlePositionChange?: (id: string, centerX: number, centerY: number) => void;
    onSubtitlePaddingChange?: (id: string, paddingX: number, paddingY: number) => void;
    currentTime: number;
    duration: number;
    isPlaying: boolean;
    activeSubtitleItems: Subtitle[];
    subtitleStackOffsets?: Map<string, number>;
    onSubtitleClick?: (id: string) => void;
    className?: string;
}

export interface VideoPlayerRef {
    seekTo: (time: number) => void;
    togglePlayPause: () => void;
    currentTime: number;
    duration: number;
    paused: boolean;
    mediaFile: File | null;
    videoDimensions: VideoDimensions | null;
}

export type VideoDimensions = {
    width: number;
    height: number;
    playerWidth: number;
    playerHeight: number;
}

const OVERLAY_LEAD_TIME = isFirefox() ? 0.15 : 0.06;
const OVERLAY_LEAD_TIME_INACTIVE = isFirefox() ? 0.06 : 0.0;

// Overlays must be mounted (at opacity 0) before the frame-level visibility toggle can
// fade them in at start - OVERLAY_LEAD_TIME, so cues are mounted this far ahead of time.
export const SUBTITLE_MOUNT_LEAD_TIME = 0.3;

const VolumeIcon = ({ volume, muted }: { volume: number; muted: boolean }) => {
    if (muted || volume === 0) {
        return (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </svg>
        );
    }
    if (volume < 0.5) {
        return (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M7 9v6h4l5 5V4l-5 5H7z"/>
            </svg>
        );
    }
    return (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
        </svg>
    );
};

const VolumeSlider = ({ volume, muted, onChange }: { volume: number; muted: boolean; onChange: (val: number) => void }) => (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-3 px-2 flex flex-col items-center">
        <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={muted ? 0 : volume}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="volume-slider h-24 cursor-pointer"
            style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
        />
    </div>
);

const VolumeControl = ({ volume, muted, isAudioOnly, onVolumeChange, onMuteToggle }: {
    volume: number;
    muted: boolean;
    isAudioOnly: boolean;
    onVolumeChange: (val: number) => void;
    onMuteToggle: () => void;
}) => {
    const [open, setOpen] = useState(false);
    const popupAbove = !isAudioOnly;

    return (
        <>
            {/* Desktop: hover to show slider, click to mute */}
            <div className="relative hidden lg:block" onMouseLeave={() => setOpen(false)}>
                <button
                    onClick={onMuteToggle}
                    onMouseEnter={() => setOpen(true)}
                    className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full"
                >
                    <VolumeIcon volume={volume} muted={muted} />
                </button>
                {open && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 pb-1 z-20">
                        <VolumeSlider volume={volume} muted={muted} onChange={onVolumeChange} />
                    </div>
                )}
            </div>

            {/* Mobile: tap to toggle slider */}
            <div className="relative lg:hidden">
                <button
                    onClick={() => setOpen(o => !o)}
                    className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full"
                >
                    <VolumeIcon volume={volume} muted={muted} />
                </button>
                {open && (
                    <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                        <div className={`absolute left-1/2 -translate-x-1/2 z-20 ${popupAbove ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                            <VolumeSlider volume={volume} muted={muted} onChange={onVolumeChange} />
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(({
    mediaFile,
    cacheKey,
    playbackSpeed,
    onTimeUpdate,
    onPlayStateChange,
    onVideoLoadingProgress,
    onVideoDimensionsChange,
    onMediaError,
    onTimeSeek,
    onPlaybackSpeedChange,
    onSubtitlePositionChange,
    onSubtitlePaddingChange,
    currentTime,
    duration,
    isPlaying,
    onSubtitleClick,
    activeSubtitleItems,
    subtitleStackOffsets,
    className = ''
}, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [useFallbackPlayer, setUseFallbackPlayer] = useState(false);
    const playPromiseRef = useRef<Promise<void> | null>(null);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [isAudioOnly, setIsAudioOnly] = useState(false);
    const isAudioOnlyRef = useRef(false);
    const [mediaUrl, setMediaUrl] = useState<string | null>(null);
    const [volume, setVolume] = useState(() => parseFloat(typeof window !== 'undefined' && localStorage.getItem('videoPlayerVolume') || '1'));
    const [muted, setMuted] = useState(() => typeof window !== 'undefined' && localStorage.getItem('videoPlayerMuted') === 'true');
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);
    const [speedMenuAbove, setSpeedMenuAbove] = useState(true);
    const [videoDimensions, setVideoDimensions] = useState<VideoDimensions|null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const speedButtonRef = useRef<HTMLButtonElement>(null);
    const activeItemsRef = useRef<Subtitle[]>(activeSubtitleItems);
    const overlayRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const lastVisibilityRef = useRef<Map<string, boolean>>(new Map());
    const isPlayingRef = useRef(isPlaying);

    useEffect(() => {
        isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    const updateSubtitleVisibility = useCallback((time: number) => {
        const leadTime = isPlayingRef.current ? OVERLAY_LEAD_TIME : OVERLAY_LEAD_TIME_INACTIVE;
        for (const subtitle of activeItemsRef.current) {
            const shouldShow = time + leadTime >= subtitle.startTimeSeconds &&
                time <= subtitle.endTimeSeconds;

            if (lastVisibilityRef.current.get(subtitle.id) !== shouldShow) {
                lastVisibilityRef.current.set(subtitle.id, shouldShow);
                const el = overlayRefs.current.get(subtitle.id);
                if (el) {
                    el.style.opacity = shouldShow ? '1' : '0';
                }
            }
        }
    }, []);

    useEffect(() => {
        activeItemsRef.current = activeSubtitleItems;
        const activeIds = new Set(activeSubtitleItems.map(item => item.id));
        for (const id of lastVisibilityRef.current.keys()) {
            if (!activeIds.has(id)) {
                lastVisibilityRef.current.delete(id);
            }
        }
        if (videoRef.current) {
            updateSubtitleVisibility(videoRef.current.currentTime);
        }
    }, [activeSubtitleItems, updateSubtitleVisibility]);

    useEffect(() => {
        if (mediaFile) {
            const url = URL.createObjectURL(mediaFile);
            setMediaUrl(url);
            return () => {
                setMediaUrl(url => {
                    if(url) {
                        URL.revokeObjectURL(url);
                    }
                    return null;
                });
                setUseFallbackPlayer(false);
                setVideoLoaded(false);
                abortControllerRef?.current?.abort();
                abortControllerRef.current = null;
                playPromiseRef.current = null;
            };
        } else {
            setMediaUrl(null);
        }
    }, [mediaFile]);

    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            video.volume = volume;
            video.muted = muted;
        }
        localStorage.setItem('videoPlayerVolume', String(volume));
        localStorage.setItem('videoPlayerMuted', String(muted));
    }, [volume, muted]);

    useEffect(() => {
        if (videoLoaded) {
            onVideoLoadingProgress(1);
        }
    }, [videoLoaded]);

    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            video.playbackRate = playbackSpeed;
        }
    }, [playbackSpeed]);

    const handleError = () => {
        if (!useFallbackPlayer && isDesktop()) {
            const video = videoRef.current;
            if (video) {
                if (mediaUrl) {
                    URL.revokeObjectURL(mediaUrl);
                    setMediaUrl(null);
                }
                video.removeAttribute('src');
                video.load();
            }
            setVideoLoaded(false)
            setUseFallbackPlayer(true);
        } else {
            setVideoLoaded(false);
            onMediaError();
        }
    };

    useEffect(() => {
        const videoElement = videoRef.current;
        if (videoElement && mediaUrl) {
            videoElement.src = mediaUrl;
            videoElement.volume = volume;
            videoElement.muted = muted;
            videoElement.load();

            let frameCallbackId: number | null = null;
            let rafId: number | null = null;

            const supportsVideoFrameCallback = 'requestVideoFrameCallback' in HTMLVideoElement.prototype;

            const useVideoFrameCallback = () => supportsVideoFrameCallback && !isAudioOnlyRef.current;

            const updateTime = () => {
                if (videoElement && !videoElement.paused) {
                    const time = videoElement.currentTime;
                    onTimeUpdate(time);
                    updateSubtitleVisibility(time);

                    if (useVideoFrameCallback()) {
                        frameCallbackId = videoElement.requestVideoFrameCallback(updateTime);
                    } else {
                        rafId = requestAnimationFrame(updateTime);
                    }
                }
            };

            const handlePlay = () => {
                onPlayStateChange(true);
                if (useVideoFrameCallback()) {
                    frameCallbackId = videoElement.requestVideoFrameCallback(updateTime);
                } else {
                    rafId = requestAnimationFrame(updateTime);
                }
            };

            const handlePause = () => {
                onPlayStateChange(false);
                if (supportsVideoFrameCallback && frameCallbackId !== null) {
                    videoElement.cancelVideoFrameCallback(frameCallbackId);
                    frameCallbackId = null;
                } else if (rafId !== null) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
            };

            const handleLoadedMetadata = () => {
                setVideoLoaded(true);
                if (videoElement.videoWidth === 0 && videoElement.videoHeight === 0) {
                    setIsAudioOnly(true);
                    isAudioOnlyRef.current = true;
                } else {
                    setIsAudioOnly(false);
                    isAudioOnlyRef.current = false;
                    updateVideoDimensions();
                }
            };

            const updateVideoDimensions = () => {
                if (videoRef.current) {
                    const { offsetWidth, offsetHeight, videoWidth, videoHeight } = videoRef.current;
                    setVideoDimensions({
                        width: videoWidth,
                        height: videoHeight,
                        playerWidth: offsetWidth,
                        playerHeight: offsetHeight
                    });
                }
            };

            const resizeObserver = new ResizeObserver(() => {
                updateVideoDimensions();
            });
            resizeObserver.observe(videoElement);

            videoElement.addEventListener('play', handlePlay);
            videoElement.addEventListener('pause', handlePause);
            videoElement.addEventListener('error', handleError);
            videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);

            const handleKeyDown = (e: KeyboardEvent) => {
                const activeElement = document.activeElement as HTMLElement;
                const isInput = activeElement?.tagName === 'INPUT' || activeElement?.isContentEditable;

                if (e.code === 'Space' && !isInput) {
                    e.preventDefault();
                    handlePlayPause();
                }
            };

            window.addEventListener('keydown', handleKeyDown);

            return () => {
                resizeObserver.disconnect();
                if (supportsVideoFrameCallback && frameCallbackId !== null) {
                    videoElement.cancelVideoFrameCallback(frameCallbackId);
                } else if (rafId !== null) {
                    cancelAnimationFrame(rafId);
                }
                videoElement.removeEventListener('play', handlePlay);
                videoElement.removeEventListener('pause', handlePause);
                videoElement.removeEventListener('error', handleError);
                videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
                window.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [mediaUrl]);

    useEffect(() => {
        if(videoDimensions && !isAudioOnly) {
            const {width, height} = videoDimensions;
            if(width > 0 && height > 0) {
                onVideoDimensionsChange({ width, height})
            }
        }
    }, [videoDimensions, isAudioOnly]);

    useEffect(() => {
        if (useFallbackPlayer && mediaFile) {
            const initRemux = async () => {
                try {
                    const ffmpeg = new FFmpegService();
                    await ffmpeg.load();

                    console.log('Using OPFS-based HLS streaming remux for compatibility');
                    const remuxService = new OPFSRemuxService(ffmpeg, opfsService);

                    const abortHandler = new AbortController();
                    abortControllerRef.current = abortHandler;
                    const remuxedFile = await remuxService.remuxToOPFS(mediaFile, cacheKey, abortHandler.signal, (progress) => {
                        const pct = progress.percent / 100
                        onVideoLoadingProgress(pct - 0.01) // account for loading metadata delay
                        console.log(`Remux progress: ${progress.percent.toFixed(1)}%`);
                    });

                    if(remuxedFile) {
                        const fileUrl = URL.createObjectURL(remuxedFile);
                        setMediaUrl(fileUrl);
                    }
                } catch (error) {
                    console.error('OPFS remux failed:', error);
                    handleError();
                }
            };

            initRemux();
        }
    }, [useFallbackPlayer, mediaFile, cacheKey]);

    const handlePlayPause = () => {
        const video = videoRef.current;
        if (!video) return;

        if (video.paused) {
            playPromiseRef.current = video.play().catch(console.error);
        } else {
            if (playPromiseRef.current !== null) {
                playPromiseRef.current.then(() => video.pause());
            } else {
                video.pause();
            }
        }
    };

    const seekTo = (time: number) => {
        const video = videoRef.current;
        if (video) {
            video.currentTime = time;
            onTimeUpdate(time);
            updateSubtitleVisibility(time);
        }
    };

    const togglePlayPause = () => {
        handlePlayPause();
    };

    useImperativeHandle(ref, () => ({
        seekTo,
        togglePlayPause,
        get currentTime() {
            return videoRef.current?.currentTime || 0;
        },
        get duration() {
            return videoRef.current?.duration || 0;
        },
        get paused() {
            return videoRef.current?.paused || true;
        },
        get mediaFile() {
            return mediaFile;
        },
        get videoDimensions() {
            if(!videoRef.current || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
                return null;
            }

            const {
                offsetWidth: playerWidth,
                offsetHeight: playerHeight,
                videoWidth: width,
                videoHeight: height,
            } = videoRef.current;
            return { width, height, playerWidth, playerHeight };
        }
    }));

    const AudioPlaceholder = () => (
        <div className="w-full hidden  max-h-[40vh] lg:h-[30vh] bg-gray-900 dark:bg-gray-800 lg:rounded-lg lg:flex flex-col items-center justify-center text-white dark:text-gray-100 p-8">
            <div className="flex flex-col items-center space-y-4">
                <svg className="w-16 h-16 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.59-.79-1.59-1.75v-3.5c0-.96.71-1.75 1.59-1.75h2.24z" />
                </svg>
                <div className="text-center">
                    <div className="text-lg font-medium mb-1">Audio File</div>
                    {mediaFile?.name && (
                        <div className="text-sm text-gray-300 dark:text-gray-400 break-all">{mediaFile.name}</div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className={`${className}`}>
            <div className="relative w-full">
                <video
                    ref={videoRef}
                    controls={false}
                    disablePictureInPicture
                    disableRemotePlayback
                    className={`w-full max-h-[40vh] lg:max-h-[50vh] md:rounded-lg ${isAudioOnly ? 'hidden' : ''}`}
                    style={{ height: 'auto', pointerEvents: 'none' }}
                    onContextMenu={(e) => e.preventDefault()}
                />
                {!isAudioOnly && videoDimensions && activeSubtitleItems.map((item) => (
                    <SubtitleOverlay
                        key={item.id}
                        ref={(el) => {
                            if (el) {
                                overlayRefs.current.set(item.id, el);
                            } else {
                                overlayRefs.current.delete(item.id);
                            }
                        }}
                        subtitle={item}
                        stackOffsetPctY={subtitleStackOffsets?.get(item.id) ?? 0}
                        playerWidth={videoDimensions.playerWidth}
                        playerHeight={videoDimensions.playerHeight}
                        videoWidth={videoDimensions.width}
                        onClick={() => onSubtitleClick?.(item.id)}
                        videoHeight={videoDimensions.height}
                        onPositionChange={(centerX, centerY) => onSubtitlePositionChange?.(item.id, centerX, centerY)}
                        onPaddingChange={(paddingX, paddingY) => onSubtitlePaddingChange?.(item.id, paddingX, paddingY)}
                    />
                ))}
            </div>
            {isAudioOnly && <AudioPlaceholder />}

            {/* Playback Controls */}
            {mediaFile && (
                <div className="py-1 mx-1 md:mx-0 md:py-0 lg:mt-3">
                    <div className="flex items-center space-x-1">
                        <div className="h-8 flex items-center">
                            <button
                                onClick={togglePlayPause}
                                className="w-8 h-8 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full"
                                title={isPlaying ? "Pause" : "Play"}
                            >
                                {isPlaying ? (
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                                    </svg>
                                ) : (
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z"/>
                                    </svg>
                                )}
                            </button>
                            <div className="relative ml-1">
                                {!showSpeedMenu ? (
                                    <Tooltip content="Playback speed" theme={{target: 'inline-flex'}} className="whitespace-nowrap text-xs">
                                        <button
                                            ref={speedButtonRef}
                                            onClick={() => {
                                                if (speedButtonRef.current) {
                                                    const rect = speedButtonRef.current.getBoundingClientRect();
                                                    setSpeedMenuAbove(rect.top > 200);
                                                }
                                                setShowSpeedMenu(true);
                                            }}
                                            className="text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded px-2 py-1 notranslate"
                                        >
                                            {(playbackSpeed * 10) % 1 === 0 ? playbackSpeed.toFixed(1) : playbackSpeed.toFixed(2)}x
                                        </button>
                                    </Tooltip>
                                ) : (
                                    <button
                                        ref={speedButtonRef}
                                        onClick={() => setShowSpeedMenu(false)}
                                        className="text-xs text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded px-2 py-1 notranslate"
                                    >
                                        {(playbackSpeed * 10) % 1 === 0 ? playbackSpeed.toFixed(1) : playbackSpeed.toFixed(2)}x
                                    </button>
                                )}
                                {showSpeedMenu && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setShowSpeedMenu(false)}
                                        />
                                        <div className={`absolute ${speedMenuAbove ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white dark:text-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg z-20`}>
                                            {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((speed) => (
                                                <button
                                                    key={speed}
                                                    onClick={() => {
                                                        onPlaybackSpeedChange(speed);
                                                        setShowSpeedMenu(false);
                                                    }}
                                                    className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 notranslate ${
                                                        speed === playbackSpeed ? 'bg-gray-100 dark:bg-gray-700/50 font-medium' : ''
                                                    }`}
                                                >
                                                    {(speed * 10) % 1 === 0 ? speed.toFixed(1) : speed.toFixed(2)}x
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <VolumeControl
                            volume={volume}
                            muted={muted}
                            isAudioOnly={isAudioOnly}
                            onVolumeChange={(val) => {
                                setVolume(val);
                                if (val > 0) setMuted(false);
                            }}
                            onMuteToggle={() => setMuted(m => !m)}
                        />
                        <div className="text-xs text-gray-600 dark:text-gray-400 w-16 text-center tabular-nums">
                            {formatTimeLabel(currentTime)}
                        </div>
                        <div className="flex-1 relative flex items-center">
                            <input
                                type="range"
                                min="0"
                                max={duration}
                                value={currentTime}
                                onChange={(e) => onTimeSeek(parseFloat(e.target.value))}
                                className="seekbar-input w-full h-1 bg-gray-300 dark:bg-gray-600 rounded-lg cursor-pointer"
                                style={{
                                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / duration) * 100}%, #d1d5db ${(currentTime / duration) * 100}%, #d1d5db 100%)`
                                }}
                            />
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 w-16 text-center tabular-nums">
                            {formatTimeLabel(duration)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
